import { IResultResponse } from '@common/api.models';
import { isValidApiBaseProps } from '@common/api.services';
import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { AxiosErrorResponse } from '@core/error.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { IItemResponse } from '@gcl/react-core';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ICreateProjectDiscussionRequest,
  IProjectDiscussion,
  IReplyProjectDiscussionRequest,
  ISearchProjectDiscussionRequest,
  IUpdateProjectDiscussionRequest,
} from './projectDiscussion.models';
import {
  createProjectDiscussionAsync,
  deleteProjectDiscussionAsync,
  getProjectDiscussionListAsync,
  IDeleteProjectDiscussionProps,
  updateProjectDiscussionRepliesAsDraftAsync,
  updateProjectDiscussionRepliesAsIssueAsync,
  updateProjectDiscussionsAsDraftAsync,
  updateProjectDiscussionsAsIssueAsync,
} from './projectDiscussion.services';

export const useFetchProjectDiscussionList = (props: ISearchProjectDiscussionRequest) => {
  return useQuery<IProjectDiscussion[], AxiosErrorResponse>(
    [props.projectId, props, FETCH_QUERIES_NAME.PROJECT_DISCUSSION_LIST],
    async () => {
      const results = await getProjectDiscussionListAsync(props);
      return results?.items || [];
    },
    {
      enabled:
        (isPositiveNumber(props.projectDesignBriefId) ||
          isPositiveNumber(props.projectTransmittalId) ||
          isPositiveNumber(props.projectDesignModificationId) ||
          isPositiveNumber(props.projectLoadingRemovalCertId) ||
          isPositiveNumber(props.projectLambethReviewerResponseId)) &&
        isValidApiBaseProps(props),
    },
  );
};

export const useCreateProjectDiscussion = () => {
  return useMutation<IItemResponse<IProjectDiscussion>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.CREATE_PROJECT_DISCUSSION],
    async (request: ICreateProjectDiscussionRequest) => {
      const result = await createProjectDiscussionAsync(request);
      return result;
    },
  );
};

export const useUpdateProjectDiscussionsAsDraft = () => {
  return useMutation<IProjectDiscussion[], AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPDATE_PROJECT_DISCUSSIONS_AS_DRAFT],
    async (request: IUpdateProjectDiscussionRequest[]) => {
      if (request.length <= 0) return [];
      const result = await updateProjectDiscussionsAsDraftAsync(request);
      return result.items || [];
    },
  );
};

export const useUpdateProjectDiscussionsAsIssue = () => {
  return useMutation<IProjectDiscussion[], AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPDATE_PROJECT_DISCUSSIONS_AS_ISSUE],
    async (request: IUpdateProjectDiscussionRequest[]) => {
      if (request.length <= 0) return [];
      const result = await updateProjectDiscussionsAsIssueAsync(request);
      return result.items || [];
    },
  );
};

export const useUpdateProjectDiscussionAsDraft = () => {
  return useMutation<IItemResponse<IProjectDiscussion>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPDATE_PROJECT_DISCUSSION_AS_DRAFT],
    async (request: IUpdateProjectDiscussionRequest) => {
      const result = await updateProjectDiscussionsAsDraftAsync([request]);
      const item = result.items && result.items.length === 1 ? result.items[0] : null;
      return { item, isSuccess: !!result } as IItemResponse<IProjectDiscussion>;
    },
  );
};

export const useUpdateProjectDiscussionAsIssue = () => {
  return useMutation<IItemResponse<IProjectDiscussion>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPDATE_PROJECT_DISCUSSION_AS_ISSUE],
    async (request: IUpdateProjectDiscussionRequest) => {
      const result = await updateProjectDiscussionsAsIssueAsync([request]);
      const item = result.items && result.items.length === 1 ? result.items[0] : null;
      return { item, isSuccess: !!result } as IItemResponse<IProjectDiscussion>;
    },
  );
};

export const useUpdateProjectDiscussionRepliesAsDraft = () => {
  return useMutation<IProjectDiscussion[], AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPDATE_PROJECT_DISCUSSION_REPLYS_AS_DRAFT],
    async (request: IReplyProjectDiscussionRequest[]) => {
      if (request.length <= 0) return [];
      const result = await updateProjectDiscussionRepliesAsDraftAsync(request);
      return result.items || [];
    },
  );
};

export const useUpdateProjectDiscussionRepliesAsIssue = () => {
  return useMutation<IProjectDiscussion[], AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPDATE_PROJECT_DISCUSSION_REPLYS_AS_ISSUE],
    async (request: IReplyProjectDiscussionRequest[]) => {
      if (request.length <= 0) return [];
      const result = await updateProjectDiscussionRepliesAsIssueAsync(request);
      return result.items || [];
    },
  );
};

export const useUpdateProjectDiscussionReplyAsDraft = () => {
  return useMutation<IItemResponse<IProjectDiscussion>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPDATE_PROJECT_DISCUSSION_REPLY_AS_DRAFT],
    async (request: IReplyProjectDiscussionRequest) => {
      const result = await updateProjectDiscussionRepliesAsDraftAsync([request]);
      const item = result.items && result.items.length === 1 ? result.items[0] : null;
      return { item, isSuccess: !!result } as IItemResponse<IProjectDiscussion>;
    },
  );
};

export const useUpdateProjectDiscussionReplyAsIssue = () => {
  return useMutation<IItemResponse<IProjectDiscussion>, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPDATE_PROJECT_DISCUSSION_REPLY_AS_ISSUE],
    async (request: IReplyProjectDiscussionRequest) => {
      const result = await updateProjectDiscussionRepliesAsIssueAsync([request]);
      const item = result.items && result.items.length === 1 ? result.items[0] : null;
      return { item, isSuccess: !!result } as IItemResponse<IProjectDiscussion>;
    },
  );
};

export const useDeleteProjectDiscussion = () => {
  return useMutation<IResultResponse, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.DELETE_PROJECT_DISCUSSION],
    async (props: IDeleteProjectDiscussionProps) => {
      const result = await deleteProjectDiscussionAsync(props);
      return result;
    },
  );
};
